import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Bell, BellOff, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pushSupported, getCurrentSubscription, enablePush, disablePush } from "@/pwa/pushClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhoneDeviceControls = () => {
  const { t } = useTranslation();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState(0);
  const supported = pushSupported();

  const refresh = async () => {
    setSubscribed(!!(await getCurrentSubscription().catch(() => null)));
    axios.get(`${API}/push/devices`).then(({ data }) => setDevices(data.count)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const run = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.message === "denied" ? t("phoneAlerts.denied") : failMsg); }
    finally { setBusy(false); refresh(); }
  };

  if (!supported) return <p data-testid="phone-alerts-unsupported" className="text-xs text-slate-500">{t("phoneAlerts.unsupported")}</p>;

  return (
    <div className="space-y-2">
      {subscribed && <p data-testid="phone-alerts-enabled-status" className="text-sm font-medium text-emerald-700">{t("phoneAlerts.enabledHere")}</p>}
      <div className="flex flex-wrap gap-2">
        {subscribed ? (
          <Button data-testid="phone-alerts-disable" variant="outline" size="sm" disabled={busy} className="rounded-sm" onClick={() => run(disablePush, null, t("phoneAlerts.enableFailed"))}>
            <BellOff className="w-3.5 h-3.5 mr-1.5" /> {t("phoneAlerts.disable")}
          </Button>
        ) : (
          <Button data-testid="phone-alerts-enable" size="sm" disabled={busy} className="rounded-sm bg-orange-600 hover:bg-orange-700" onClick={() => run(enablePush, t("phoneAlerts.enabledHere"), t("phoneAlerts.enableFailed"))}>
            <Bell className="w-3.5 h-3.5 mr-1.5" /> {busy ? t("phoneAlerts.enabling") : t("phoneAlerts.enable")}
          </Button>
        )}
        <Button data-testid="phone-alerts-test" variant="outline" size="sm" disabled={busy || devices === 0} className="rounded-sm" onClick={() => run(() => axios.post(`${API}/push/test`), t("phoneAlerts.testSent"), t("phoneAlerts.testFailed"))}>
          <Send className="w-3.5 h-3.5 mr-1.5" /> {t("phoneAlerts.test")}
        </Button>
      </div>
      <p data-testid="phone-alerts-device-count" className="text-xs text-slate-500">{t("phoneAlerts.devices", { count: devices })}</p>
    </div>
  );
};
